import { useState, useRef, useCallback } from 'react';

export interface FileUploadState {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  errorMessage?: string;
}

interface UseSequentialFileUploadProps {
  documentKey?: string;
  loanId: string;
  initialDocumentId?: string;
  state?: string;
  expiration?: string;
  name?: string;
  isMultipleFiles?: boolean;
  onUploadFile: (payload: any) => Promise<any>;
  onComplete?: () => void;
  additionalPayload?: Record<string, any>;
}

export const useSequentialFileUpload = ({
  documentKey,
  loanId,
  initialDocumentId,
  state,
  expiration,
  name,
  isMultipleFiles = true,
  onUploadFile,
  onComplete,
  additionalPayload = {}
}: UseSequentialFileUploadProps) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const documentIdRef = useRef(initialDocumentId);

  // Actualizar el documentId si cambia externamente
  const updateDocumentId = useCallback((id: string) => {
    documentIdRef.current = id;
  }, []);

  const uploadFile = async (fileState: FileUploadState): Promise<void> => {
    setFiles(prev => prev.map(f =>
      f.id === fileState.id ? { ...f, status: 'uploading' } : f
    ));
    setUpdateCount(prev => prev + 1);

    try {
      // 🔴 SIMULACIÓN DE ERROR PARA TESTING
      // Solo falla si tiene 'error' en el nombre Y es el primer intento
      const currentFile = files.find(f => f.id === fileState.id);
      const attemptCount = (currentFile as any)?.attemptCount || 0;

      if (fileState.file.name.toLowerCase().includes('error') && attemptCount === 0) {
        // Incrementar contador de intentos
        setFiles(prev => prev.map(f =>
          f.id === fileState.id
            ? { ...f, attemptCount: 1 } as any
            : f
        ));

        await new Promise(resolve => setTimeout(resolve, 1000)); // Simular delay de upload
        throw new Error('Upload failed: filename contains "error"');
      }
      const payload: any = {
        _id: documentIdRef.current,
        key: documentKey,
        loan: loanId,
        state,
        ...additionalPayload
      };

      if (expiration) payload.expiration = expiration;
      if (name) payload.name = name;

      if (isMultipleFiles) {
        payload.files = [fileState.file];
      } else {
        payload.file = fileState.file;
      }

      const result = await onUploadFile(payload);

      // Si no teníamos ID y obtuvimos uno, guardarlo
      const receivedId = result?.data?.CreateLoanDoc?._id;
      
      if (!documentIdRef.current && receivedId) {
        documentIdRef.current = receivedId;
      }

      setFiles(prev => prev.map(f =>
        f.id === fileState.id ? { ...f, status: 'success' } : f
      ));
      setUpdateCount(prev => prev + 1);

    } catch (error: any) {
      setFiles(prev => prev.map(f =>
        f.id === fileState.id
          ? { ...f, status: 'error', errorMessage: error.message || 'Upload failed' }
          : f
      ));
      setUpdateCount(prev => prev + 1);
    }
  };

  const startQueue = async (filesToProcess?: FileUploadState[]): Promise<void> => {
    const queueFiles = filesToProcess || files;
    
    setIsProcessing(true);

    const pendingFiles = queueFiles.filter(
      f => f.status === 'pending' || f.status === 'error'
    );

    for (const fileState of pendingFiles) {
      await uploadFile(fileState);
    }

    if (onComplete) {
      await onComplete();
    }
    
    setIsProcessing(false);
  };

  const retryFile = async (fileId: string): Promise<void> => {
    const fileState = files.find(f => f.id === fileId);
    if (!fileState) return;

    await uploadFile(fileState);
  };

  const addFiles = useCallback((newFiles: File[]): FileUploadState[] => {
    const fileStates: FileUploadState[] = newFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending' as const
    }));

    setFiles(prev => [...prev, ...fileStates]);
    setUpdateCount(prev => prev + 1);
    
    // Retornar los fileStates para que puedan ser procesados inmediatamente
    return fileStates;
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const clearSuccessfulFiles = useCallback(() => {
    // Solo limpiar archivos exitosos, mantener los que tienen error
    setFiles(prev => prev.filter(f => f.status === 'error'));
    setUpdateCount(prev => prev + 1);
  }, []);

  const hasErrors = files.some(f => f.status === 'error');
  const allSuccess = files.length > 0 && files.every(f => f.status === 'success');
  const hasPending = files.some(f => f.status === 'pending' || f.status === 'error');

  return {
    files,
    isProcessing,
    hasErrors,
    allSuccess,
    hasPending,
    startQueue,
    retryFile,
    addFiles,
    removeFile,
    clearFiles,
    clearSuccessfulFiles,
    updateDocumentId,
    updateCount
  };
};

